import { drawExtensionIcon } from '@/extensions/pixelIcons';
import { z } from 'zod';
import { extensionCatalog, type DigitilesExtension } from '../catalog';
import { configureTile } from '../configuration';
import { watchActiveExtensionNodes } from '../activeNodes';
import { clockedBuild, clockIo, clockNode, watchClocks } from '../clocked';
import { InputType, type TileDefinition } from '@/models/tiles/TileDefinition';
import { CircuitType, Side } from '@/schemas/common';
import { TileSideIOType } from '@/schemas/tiles';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import { colors } from '@/styles';

export const STORAGE_READ = 'extension:digitiles.storage/read' as const;
export const STORAGE_WRITE = 'extension:digitiles.storage/write' as const;
/** Separate from project/settings keys. Shared by circuits on this browser origin. */
export const STORAGE_PREFIX = 'digitiles.extension.storage:';
const keySchema = z
  .string()
  .trim()
  .min(1, 'Enter a storage key.')
  .max(128, 'Use at most 128 characters.');
const bitSchema = z.number().int().min(0).max(7);
const configSchema = z.object({ key: keySchema, bit: bitSchema });
const runtimeSchema = configSchema.extend({ enabled: z.boolean(), value: z.boolean() });
type Config = z.infer<typeof configSchema>;
const defaults = (): Config => ({ key: 'register', bit: 0 });
function definition(
  type: typeof STORAGE_READ | typeof STORAGE_WRITE,
): TileDefinition<Config> {
  const write = type === STORAGE_WRITE;
  const ioSides = {
    [Side.TOP]: { type: TileSideIOType.NONE, forceConnection: false },
    [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: true },
    [Side.BOTTOM]: clockIo,
    [Side.LEFT]: {
      type: write ? TileSideIOType.INPUT : TileSideIOType.NONE,
      forceConnection: write,
    },
  };
  return {
    type,
    name: write ? 'Local storage write' : 'Local storage read',
    canRotate: true,
    stateSchema: configSchema,
    createDefaultState: defaults,
    graphConfiguration: (state) => ({ key: state.key, bit: state.bit }),
    ioSides,
    buildAndConnectNodes: clockedBuild(type, write),
    createContextMenuItems: ({ instance, chipEditorModel }) => [
      {
        label: `Storage key (${instance.state.key})`,
        action: async (takeInput) => {
          try {
            const key = await takeInput(
              InputType.STRING,
              'Storage key — shared on this browser origin',
              instance.state.key,
              true,
              (value) => {
                const result = keySchema.safeParse(value);
                return {
                  isValid: result.success,
                  errorMessage: result.error?.issues[0]?.message,
                };
              },
            );
            configureTile(
              instance,
              chipEditorModel,
              { key: keySchema.parse(key) },
              'Change storage key',
            );
          } catch {
            /* Cancel keeps the key. */
          }
        },
      },
      {
        label: `Storage bit (${instance.state.bit})`,
        action: async (takeInput) => {
          try {
            const bit = await takeInput(
              InputType.NUMBER,
              'Byte bit (0–7; 7 is the high bit)',
              instance.state.bit,
              true,
              (value) => ({
                isValid: bitSchema.safeParse(Number(value)).success,
                errorMessage: 'Enter a whole number from 0 to 7.',
              }),
            );
            configureTile(
              instance,
              chipEditorModel,
              { bit: bitSchema.parse(Number(bit)) },
              'Change storage bit',
            );
          } catch {
            /* Cancel keeps the bit. */
          }
        },
      },
    ],
    graphics: {
      type,
      draw(g, { meta, connections, context }) {
        defaultTileGraphics(g);
        connectionGraphics(
          g,
          colors.signalColor,
          colors.noSignalColor,
          meta,
          connections,
          ioSides,
          context,
        );
        drawExtensionIcon(
          g,
          'storage',
          meta.outputs.right ? 0x8bd6ff : 0x8192a4,
          write ? 'write' : 'read',
        );
      },
    },
  };
}
function readByte(key: string) {
  const raw = localStorage.getItem(key);
  if (raw === null) return 0;
  if (!/^(0|[1-9]\d{0,2})$/.test(raw) || Number(raw) > 255)
    throw new Error(
      'Stored value must be a byte from 0 to 255. Choose another key or correct its value.',
    );
  return Number(raw);
}
export const storageExtension: DigitilesExtension = {
  id: 'digitiles.storage',
  name: 'Local storage',
  version: '1.0.0',
  description:
    'Persist byte bits in this browser. Bottom: rising clock; write left: data; right: last sampled bit. Keys are shared across projects on this origin. Enable live I/O to access storage.',
  capabilities: ['storage'],
  tiles: [STORAGE_READ, STORAGE_WRITE].map((type) => ({
    definition: definition(type),
    circuits: [CircuitType.ChipLogic],
  })),
  nodes: [STORAGE_READ, STORAGE_WRITE].flatMap((type) => [
    clockNode(type),
    {
      type,
      stateSchema: runtimeSchema.transform((state) => ({
        ...state,
        enabled: false,
        value: false,
      })),
      configurationKeys: ['key', 'bit', 'enabled'],
      createDefaultState: () => ({ ...defaults(), enabled: false, value: false }),
      process: (_input: boolean, state: z.infer<typeof runtimeSchema>) =>
        state.enabled && state.value,
    },
  ]),
  activate(app) {
    let previous: import('../activeNodes').ActiveExtensionNode[] = [];
    const unwatch = watchActiveExtensionNodes(
      app,
      [STORAGE_READ, STORAGE_WRITE],
      (targets) => {
        const retained = new Set(targets.map((t) => t.node));
        for (const target of previous)
          if (!retained.has(target.node)) {
            Object.assign(target.node.state, { enabled: false, value: false });
            if (target.simulation.graph.nodes.get(target.node.id) === target.node)
              target.simulation.triggerUpdate(target.node.id);
          }
        previous = targets;
        for (const target of targets) {
          target.node.state.enabled = true;
          target.simulation.triggerUpdate(target.node.id);
        }
      },
    );
    const clocks = [STORAGE_READ, STORAGE_WRITE].map((type) =>
      watchClocks(app, type, (target) => {
        const state = target.node.state as z.infer<typeof runtimeSchema>;
        if (!state.enabled) return;
        let next = false;
        try {
          const key = STORAGE_PREFIX + state.key;
          const byte = readByte(key);
          if (type === STORAGE_WRITE) {
            const input = target.graph.getNodeInput(target.node.id);
            const updated = input ? byte | (1 << state.bit) : byte & ~(1 << state.bit);
            if (updated !== byte) localStorage.setItem(key, String(updated));
            next = input;
          } else next = (byte & (1 << state.bit)) !== 0;
          extensionCatalog.setError('digitiles.storage', null);
        } catch (error) {
          extensionCatalog.setError(
            'digitiles.storage',
            `Storage operation failed: ${error instanceof Error ? error.message : String(error)}`,
          );
        }
        if (state.value !== next) {
          state.value = next;
          target.simulation.triggerUpdate(target.node.id);
        }
      }),
    );
    return () => {
      clocks.forEach((stop) => stop());
      unwatch();
    };
  },
};
